import React, { useMemo } from "react";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { SkeletonLoader } from "@whoosh/design-system/src/components/display";
import {
  Box,
  HStack,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { getBookingSummaryDisplayConfig } from "../../bookingSummaryDisplayConfig";
import {
  BOOKING_SUMMARY_CONTENT_MAX_WIDTH,
  useIsBookingSummaryNarrow,
  useIsBookingSummaryStacked,
} from "../../bookingSummaryWidth";
import { BookingPaymentSkeleton } from "./BookingPaymentSkeleton";
import { BookingSummaryContentSkeleton } from "./BookingSummaryContentSkeleton";
import { BookingSummaryHeaderSkeleton } from "./BookingSummaryHeaderSkeleton";

type Props = {
  supportsPrivateBookings: boolean;
  clubSupportsPrivateBookings: boolean;
  isLessonBooking?: boolean;
  isMultipleBooking?: boolean;
};

export const BookingSummarySkeleton = ({
  supportsPrivateBookings,
  clubSupportsPrivateBookings,
  isLessonBooking = false,
  isMultipleBooking = false,
}: Props) => {
  const isNarrow = useIsBookingSummaryNarrow();
  const isStacked = useIsBookingSummaryStacked();
  const insets = useSafeAreaInsets();

  const {
    shouldShowChargesSummary,
    shouldShowBookingPayment,
    shouldShowFooter,
  } = useMemo(
    () =>
      getBookingSummaryDisplayConfig({
        isMobile: isStacked,
        clubSupportsPrivateBookings: Boolean(supportsPrivateBookings),
        isPrivateLessonRequest: false,
        isLessonBooking,
      }),
    [isLessonBooking, isStacked, supportsPrivateBookings]
  );

  const paddingStyle = useMemo(
    () => ({ paddingTop: insets.top, paddingBottom: insets.bottom }),
    [insets.bottom, insets.top]
  );

  return (
    <HStack
      testID="booking-summary-skeleton-root"
      width="100%"
      flex={1}
      style={paddingStyle}
      backgroundColor="surface-secondary"
    >
      {shouldShowBookingPayment ? (
        <Box
          backgroundColor={
            clubSupportsPrivateBookings
              ? "surface-secondary"
              : "surface-primary"
          }
          flexGrow={1}
          flexShrink={1}
          flexBasis={0}
        />
      ) : null}
      <Box
        backgroundColor={
          supportsPrivateBookings ? "surface-secondary" : "surface-primary"
        }
        flexShrink={1}
        flexGrow={1}
      >
        <Box
          testID="booking-summary-skeleton-content"
          maxWidth={BOOKING_SUMMARY_CONTENT_MAX_WIDTH}
          marginLeft="auto"
          height="100%"
          width="100%"
          marginTop={isNarrow ? 0 : 3}
          backgroundColor={"surface-primary"}
          marginRight={shouldShowBookingPayment ? 3 : "auto"}
          px={isStacked ? 3 : shouldShowBookingPayment ? 0 : 4}
          justifyContent={isStacked ? "space-between" : "flex-start"}
          py={isStacked ? 5 : 6}
        >
          <VStack gap={5}>
            <BookingSummaryHeaderSkeleton />
            {isMultipleBooking ? (
              <>
                <BookingSummaryContentSkeleton isMobile={isStacked} />
                <BookingSummaryContentSkeleton isMobile={isStacked} />
              </>
            ) : (
              <BookingSummaryContentSkeleton isMobile={isStacked} />
            )}
            {shouldShowChargesSummary && !isStacked ? (
              <Box mt={6}>
                <SkeletonLoader
                  testID="booking-summary-skeleton-charges"
                  height={220}
                  width="100%"
                  borderRadius="4"
                />
              </Box>
            ) : null}
          </VStack>
          {shouldShowFooter ? (
            <Box mt={3}>
              <SkeletonLoader
                testID="booking-summary-skeleton-footer"
                height={92}
                width="100%"
                borderRadius="4"
              />
            </Box>
          ) : null}
        </Box>
      </Box>

      {shouldShowBookingPayment ? (
        <BookingPaymentSkeleton isMobile={isStacked} />
      ) : null}
      {shouldShowBookingPayment ? (
        <Box
          backgroundColor={"surface-secondary"}
          flexGrow={1}
          flexShrink={1}
          flexBasis={0}
        />
      ) : null}
    </HStack>
  );
};
